import {faCheckCircle} from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import {faMagnifyingGlass} from "@fortawesome/pro-solid-svg-icons/faMagnifyingGlass";
import {faPlus} from "@fortawesome/pro-solid-svg-icons/faPlus";
import {faPoundSign} from "@fortawesome/pro-solid-svg-icons/faPoundSign";
import {faTrash} from "@fortawesome/pro-solid-svg-icons/faTrash";
import { faXmarkCircle } from "@fortawesome/pro-solid-svg-icons/faXmarkCircle";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    MailItemDescriptionForwardingBatchType,
} from "@twigsby/client-data/lib/Types/ForwardingBatchTypes";
import {
    Button,
    ButtonSize,
    ColourSolid,
    CountryCodeControl,
    FlexGap,
    FormWrapperMinimum,
    GenericLoader,
    StackHorizontal,
    ToggleSwitch,
    ToggleType
} from "@twigsby/frontend-framework";
import {
    Controller,
    FormProvider,
    useFieldArray,
    useForm
} from "@twigsby/frontend-framework/lib/ReactHookForm";
import React, { useEffect, useRef, useState} from "react";
import {ManualEventOnly} from "../../../Events";
import {useForwardingBatchProvider} from "../../../Providers/ForwardingBatchProvider";
import {useSite} from "../../../Providers/SiteInterfaceProvider";
import {BatchMailItemEntryDescriptionType, BatchMailItemEntryType} from "../../../Types/ForwardingBatchTypes";
import {MailItemThumbnails, MailPartTypeEnum} from "../../Mail/MailItemThumbnails";

const BatchMailItemEntry = (props: BatchMailItemEntryType) => {
    const {
        currentBatchData,
        DeleteMailItemDescription
    } = useForwardingBatchProvider();

    const { setPdfViewer } = useSite();

    const formMethods = useForm({
        mode: "onChange"
    });

    const { fields, remove, append } = useFieldArray({
        control: formMethods.control,
        name: `items.[${props.mailItemIndex}].descriptions`,
        shouldUnregister: true
    });

    const InsertDescription = () => {
        const newData: MailItemDescriptionForwardingBatchType = {
            descriptionType: 1,
            description: '',
            quantity: 0,
            value: 0,
            commodityCode: '',
            countryOfManufacture: '',
        };

        append({ mailDescription: newData});
    }

    const RemoveDescription = (descriptionId: number, index: number) => {
        if(descriptionId) {
            DeleteMailItemDescription(props.mailUuid, descriptionId);
        }
        remove(index);
    }

    const MapDescriptionsToFields = () => {
        if(props.descriptions.length === 0) {
            InsertDescription();
        } else {
            props.descriptions.forEach((description) => append({ mailDescription: description}))
        }
    }

    const CheckMailItemsDocumentsOnly = () => {
        fields.forEach((el) => console.log(el))
    }

    useEffect(() => {
        formMethods.reset()
    }, [currentBatchData]);

    useEffect(() => {
        if(props.descriptions) {
            MapDescriptionsToFields();
        }
    }, []);

    return (
        <div className={`forwarding-batch-item ${props.readOnly ? 'batch-read-only' : ''}`}>
            <div className='forwarding-batch-item-entry-title'>
                {props.trackingId}
            </div>
            <div
                className={'forwarding-batch-item-entry'}
                style={ !formMethods.formState.isValid ? { border: '2px solid var(--alert-border)' } : {} }
            >
                <div className={`forwarding-batch-item-entry-overview`}>
                    <div className={props.files?.length > 1 ? `thumbnail-stack` : ''}>
                        <MailItemThumbnails mailType={MailPartTypeEnum['Mail-Envelopes']} files={props.files} onClickCallback={(link) => setPdfViewer(true, link)} />
                        <MailItemThumbnails mailType={MailPartTypeEnum['Mail-Contents']} files={props.files} onClickCallback={(link) => setPdfViewer(true, link)} hideMissing={true} />
                    </div>
                    <div className={'forwarding-batch-item-entry-meta'}>
                        {fields.map((description: any, index) =>
                            <BatchMailItemEntryDescription
                                key={`maildesc_${description.id}`}

                                mailDescription={description.mailDescription}
                                index={index}
                                mailItemIndex={props.mailItemIndex}

                                mailUuid={props.mailUuid}

                                removable={fields.length > 1}
                                removeDescriptionCallback={() => RemoveDescription(description.mailDescription.id, index)}
                                readOnly={props.readOnly}
                                itemDocumentsOnlyCallback={() => CheckMailItemsDocumentsOnly()}
                            />
                        )}

                        {!props.readOnly && (
                            <StackHorizontal gap={FlexGap.ExtraSmall}>
                                <Button
                                    title={'Add Contents'}
                                    squared={true}
                                    colour={ColourSolid.Primary}
                                    icon={faPlus}
                                    onClick={() => InsertDescription()}
                                    disabled={props.readOnly}
                                />

                                {props.removable && (
                                    <Button
                                        title={'Remove from Batch'}
                                        squared={true}
                                        icon={faTrash}
                                        onClick={() => props.removeMailItem()}
                                        disabled={props.readOnly}
                                    />
                                )}
                            </StackHorizontal>
                        )}

                    </div>
                </div>
            </div>
        </div>
    )
}

const BatchMailItemEntryDescription = (props: BatchMailItemEntryDescriptionType) => {
    const {
        documentsOnly,
        showHarmony,
        OpenHarmonyLookup,
        AddMailItemDescription,
        UpdateMailItemDescription
    } = useForwardingBatchProvider();

    const formMethods = useForm({
        defaultValues: {
            id: props.mailDescription.id,
            description: props.mailDescription.description,
            descriptionType: props.mailDescription.descriptionType ? props.mailDescription.descriptionType : 1,
            originCountryCode: props.mailDescription.originCountryCode || null,
            commodityCode: props.mailDescription.commodityCode || null,
            value: props.mailDescription.value,
            quantity: props.mailDescription.quantity,
        },
        mode: 'all'
    });

    const [ entryDocumentsOnly, setEntryDocumentsOnly ] = useState<boolean>(documentsOnly ? documentsOnly : props.mailDescription.descriptionType === 3);

    const [ saving, setSaving ] = useState<boolean>(false);

    const commodityInputRef = useRef<HTMLInputElement | null>(null);
    const formRef = useRef<HTMLFormElement | null>(null);

    const HandleSave = (data: any) => {
        setSaving(true);

        if(!data.id) {
            AddMailItemDescription(props.mailUuid, data)
                .then((r) => {
                    if(r.descriptionId) {
                        formMethods.setValue('id', r.descriptionId);
                    } else {
                        Object.entries(r).map((entry: any) => {
                            entry.forEach((error: string) => {
                                formMethods.setError(entry[0], {type: 'custom', message: `${error}`})
                            })
                        })
                    }
                })
                .finally(() => setSaving(false));
        } else {
            UpdateMailItemDescription(props.mailUuid, data.id, data)
                .then((r) => {
                    setSaving(false);
                }
            )
        }
    };

    const HandleBlur = () => {
        formMethods.handleSubmit((data) => HandleSave(data));
    };

    const HandleDocumentsOnlyChange = (value: boolean, input?: any) => {
        formMethods.setValue('descriptionType', value ? 3 : 1);

        if(input) {
            ManualEventOnly('blur', formRef.current);
        }

        setEntryDocumentsOnly(value);
        HandleBlur();

        props.itemDocumentsOnlyCallback();
    }

    const HandleBatchDocumentsOnlyChange = (value: boolean) => {
        formMethods.setValue('descriptionType', value ? 3 : 1);

        setEntryDocumentsOnly(value);
        HandleBlur();
    }

    useEffect(() => {
        HandleBatchDocumentsOnlyChange(documentsOnly);
    }, [documentsOnly]);

    return (
        <FormProvider {...formMethods}>
            <form id={`batchMailItemDescription_${props.mailItemIndex}`} ref={formRef} onBlur={formMethods.handleSubmit(HandleSave)}>
                <fieldset disabled={saving || props.readOnly}>

                    <div className={'forwarding-batch-item-entry-meta-entry'}>
                        <Controller
                            control={formMethods.control}
                            name={`descriptionType`}
                            defaultValue={props.mailDescription.descriptionType || 1}
                            render={() =>
                                <ToggleSwitch
                                    type={ToggleType.Checkbox}
                                    name={`descriptionType`}
                                    label={'Documents Only?'}
                                    defaultChecked={documentsOnly ? documentsOnly : entryDocumentsOnly}
                                    onChange={(e, input) => {
                                        HandleDocumentsOnlyChange(!entryDocumentsOnly, input);
                                    }}
                                />
                            }
                        />

                        {!entryDocumentsOnly && (
                            <Controller
                                control={formMethods.control}
                                name={`description`}
                                defaultValue={props.mailDescription.description}
                                shouldUnregister={true}
                                rules={!entryDocumentsOnly ? undefined : {required: 'Description is Required'}}
                                render={({field, fieldState}) =>
                                    <FormWrapperMinimum
                                        hideValidationMessage={true}
                                        invalid={fieldState.invalid}
                                        validationMessage={fieldState.error?.message}
                                    >
                                        <input
                                            type={'text'}
                                            placeholder={'Description'}
                                            maxLength={90}
                                            defaultValue={field.value}
                                            onChange={(e) => field.onChange(e.currentTarget.value)}
                                            onBlur={() => HandleBlur()}
                                        />
                                    </FormWrapperMinimum>
                                }
                            />
                        )}

                        <div>
                            <Controller
                                control={formMethods.control}
                                name={`quantity`}
                                defaultValue={props.mailDescription.quantity}
                                rules={{required: 'Quantity is Required', min: 0 }}
                                render={({field, fieldState}) =>
                                    <FormWrapperMinimum
                                        invalid={fieldState.invalid}
                                        validationMessage={fieldState.error?.message}
                                    >
                                        <input
                                            type={'number'}
                                            placeholder={'Quantity'}
                                            min={0}
                                            defaultValue={field.value}
                                            onChange={(e) => field.onChange(parseInt(e.currentTarget.value))}
                                            onBlur={() => HandleBlur()}
                                        />
                                    </FormWrapperMinimum>
                                }
                            />
                        </div>

                        {!entryDocumentsOnly && (
                            <Controller
                                control={formMethods.control}
                                name={`value`}
                                defaultValue={props.mailDescription.value}
                                shouldUnregister={true}
                                rules={{required: 'Value is Required', min: 0.01 }}
                                render={({field, fieldState}) =>
                                    <FormWrapperMinimum
                                        invalid={fieldState.invalid}
                                        validationMessage={fieldState.error?.message}
                                        addonStart={<Button title={'£'} size={ButtonSize.Small} icon={faPoundSign}
                                                            iconOnly={true} disabled={true}/>}
                                    >
                                        <input
                                            type={'number'}
                                            placeholder={'Value'}
                                            min={0.1}
                                            step={0.1}
                                            defaultValue={field.value}
                                            onChange={(e) => field.onChange(parseInt(e.currentTarget.value))}
                                            onBlur={() => HandleBlur()}
                                        />
                                    </FormWrapperMinimum>
                                }
                            />
                        )}

                        {!entryDocumentsOnly && showHarmony && (
                            <Controller
                                control={formMethods.control}
                                name={`commodityCode`}
                                defaultValue={props.mailDescription.commodityCode}
                                shouldUnregister={true}
                                rules={{required: 'Commodity Code is Required'}}
                                render={({field, fieldState}) =>
                                    <FormWrapperMinimum
                                        invalid={fieldState.invalid}
                                        validationMessage={fieldState.error?.message}
                                        addonEnd={<Button
                                            title={'Search for Commodity Codes'}
                                            size={ButtonSize.Small}
                                            icon={faMagnifyingGlass}
                                            iconOnly={true}
                                            onClick={() => OpenHarmonyLookup(commodityInputRef.current)}
                                        />}
                                    >
                                        <input
                                            ref={commodityInputRef}
                                            type={'number'}
                                            placeholder={'Commodity Code'}
                                            pattern="\d{8,10}"
                                            min={8}
                                            max={10}
                                            defaultValue={field.value ? [field.value] : undefined}
                                            onChange={(e) => {
                                                console.log('onChange', e.currentTarget.value);
                                                field.onChange(e.currentTarget.value)
                                            }}
                                            onBlur={() => HandleBlur()}
                                        />
                                    </FormWrapperMinimum>
                                }
                            />
                        )}

                        {!entryDocumentsOnly && showHarmony && (
                            <Controller
                                control={formMethods.control}
                                name={`originCountryCode`}
                                defaultValue={props.mailDescription.originCountryCode}
                                rules={{required: 'Country Code is Required'}}
                                render={({field, fieldState, formState}) =>
                                    <FormWrapperMinimum
                                        hideValidationMessage={true}
                                        invalid={fieldState.invalid}
                                        validationMessage={fieldState.error?.message}
                                    >
                                        <CountryCodeControl
                                            placeholder={'Country of Origin'}
                                            name={`originCountryCode`}
                                            forwardValuesCallback={(val) => {
                                                field.onChange(val[0])
                                                HandleBlur()
                                            }}
                                            defaultValues={field.value ? [field.value] : undefined}
                                            singleSelection={true}
                                        />
                                    </FormWrapperMinimum>
                                }
                            />
                        )}

                        {props.removable && (
                            <Button
                                title={'Remove Contents'}
                                squared={true}
                                colour={ColourSolid.Secondary}
                                size={ButtonSize.Small}
                                icon={faTrash}
                                iconOnly={true}
                                onClick={() => props.removeDescriptionCallback()}
                            />
                        )}

                        {saving ? (
                            <GenericLoader size={"2x"}/>
                        ) : formMethods.formState.isValid ? (
                            <FontAwesomeIcon icon={faCheckCircle} color={'var(--success-bg)'} size={"2x"}/>
                        ) : (
                            <FontAwesomeIcon icon={faXmarkCircle} color={'var(--alert-bg)'} size={"2x"}/>
                        )}

                    </div>
                </fieldset>
            </form>
        </FormProvider>
    )
}

export {
    BatchMailItemEntry
}
